/**
 * Typed client for the Connected apps (assistant MCP) surfaces
 * (ADR-0261 §2.4, §2.5, §5).
 *
 *   GET    /v1/oauth/grants
 *            → `ConnectedAppsListResponse`. Acting sessions get
 *              `grants: []` (consent / revocation is a home-session
 *              action). `enabled: false` when the feature flag is off.
 *   DELETE /v1/oauth/grants/:grant_id
 *            → 204 on success; 404 unknown; 403 under an acting
 *              session.
 *   GET    /v1/oauth/grants/:grant_id/calls?page&page_size&q&sort&dir
 *            → `ConnectedAppCallsResponse`, the connection's tool-call
 *              log (searchable, sortable, paged). 404 when the caller
 *              may not manage that connection.
 *   GET    /v1/oauth/connect/:request_id
 *            → `ConnectRequestPreview` for the signed-in user.
 *   POST   /v1/oauth/connect/:request_id/approve  { scopes }
 *   POST   /v1/oauth/connect/:request_id/deny
 *            → `ConnectDecisionResponse` carrying the redirect back
 *              to the assistant client.
 *
 * Each function collapses the HTTP outcomes into a discriminated
 * union so the calling view branches on `kind`. The 503
 * `temporarily_unavailable` and the 404 `request_expired` have their
 * own union members because the UI copy differs.
 */

import type {
  ConnectDecisionResponse,
  ConnectRequestPreview,
  ConnectedAppCallSort,
  ConnectedAppCallsResponse,
  ConnectedAppsListResponse,
} from '@sill/types/api';
import { AuthRateLimitError, AuthUnauthorizedError } from '@sill/types/api';

import { ApiError, type HttpClient } from './http-client.js';

/* ---------- listConnectedApps ---------- */

export type ListConnectedAppsResult =
  | { readonly kind: 'ok'; readonly response: ConnectedAppsListResponse }
  | { readonly kind: 'disabled' }
  | { readonly kind: 'forbidden' }
  | { readonly kind: 'error'; readonly message: string };

export async function listConnectedApps(
  http: HttpClient,
): Promise<ListConnectedAppsResult> {
  try {
    const res = await http.request<ConnectedAppsListResponse>({
      method: 'GET',
      path: '/v1/oauth/grants',
    });
    if (res === undefined || !Array.isArray(res.grants) || typeof res.enabled !== 'boolean') {
      return { kind: 'error', message: 'Empty response body.' };
    }
    if (!res.enabled) return { kind: 'disabled' };
    return { kind: 'ok', response: res };
  } catch (err) {
    return mapListError(err);
  }
}

/* ---------- revokeConnectedApp ---------- */

export type RevokeConnectedAppResult =
  | { readonly kind: 'ok' }
  | { readonly kind: 'not_found' }
  | { readonly kind: 'forbidden' }
  | { readonly kind: 'error'; readonly message: string };

export async function revokeConnectedApp(
  http: HttpClient,
  grant_id: string,
): Promise<RevokeConnectedAppResult> {
  try {
    await http.request<void>({
      method: 'DELETE',
      path: `/v1/oauth/grants/${encodeURIComponent(grant_id)}`,
    });
    return { kind: 'ok' };
  } catch (err) {
    if (err instanceof ApiError) {
      if (err.kind === 'not_found') return { kind: 'not_found' };
      if (err.kind === 'forbidden') return { kind: 'forbidden' };
      return { kind: 'error', message: explainApiError(err) };
    }
    if (err instanceof AuthRateLimitError) {
      return { kind: 'error', message: 'Too many requests — wait a moment and retry.' };
    }
    return { kind: 'error', message: err instanceof Error ? err.message : 'Network error.' };
  }
}

/* ---------- listConnectedAppCalls ---------- */

export interface ConnectedAppCallsQuery {
  readonly page: number;
  readonly page_size?: number;
  readonly q?: string;
  readonly sort: ConnectedAppCallSort;
  readonly dir: 'asc' | 'desc';
}

export type ListConnectedAppCallsResult =
  | { readonly kind: 'ok'; readonly response: ConnectedAppCallsResponse }
  | { readonly kind: 'not_found' }
  | { readonly kind: 'error'; readonly message: string };

export async function listConnectedAppCalls(
  http: HttpClient,
  grant_id: string,
  query: ConnectedAppCallsQuery,
): Promise<ListConnectedAppCallsResult> {
  const params = new URLSearchParams();
  params.set('page', String(query.page));
  if (query.page_size !== undefined) params.set('page_size', String(query.page_size));
  const q = query.q?.trim() ?? '';
  if (q.length > 0) params.set('q', q);
  params.set('sort', query.sort);
  params.set('dir', query.dir);
  try {
    const res = await http.request<ConnectedAppCallsResponse>({
      method: 'GET',
      path: `/v1/oauth/grants/${encodeURIComponent(grant_id)}/calls?${params.toString()}`,
    });
    if (res === undefined || !Array.isArray(res.calls) || typeof res.total !== 'number') {
      return { kind: 'error', message: 'Empty response body.' };
    }
    return { kind: 'ok', response: res };
  } catch (err) {
    if (err instanceof ApiError) {
      if (err.kind === 'not_found') return { kind: 'not_found' };
      return { kind: 'error', message: explainApiError(err) };
    }
    if (err instanceof AuthRateLimitError) {
      return { kind: 'error', message: 'Too many requests — wait a moment and retry.' };
    }
    return { kind: 'error', message: err instanceof Error ? err.message : 'Network error.' };
  }
}

/* ---------- previewConnectRequest ---------- */

export type PreviewConnectRequestResult =
  | { readonly kind: 'ok'; readonly preview: ConnectRequestPreview }
  | { readonly kind: 'expired' }
  | { readonly kind: 'forbidden'; readonly reason: 'acting_session' | 'support_session' | 'home_session_required' | 'other' }
  | { readonly kind: 'disabled' }
  | { readonly kind: 'unauthorized' }
  | { readonly kind: 'error'; readonly message: string };

export async function previewConnectRequest(
  http: HttpClient,
  request_id: string,
): Promise<PreviewConnectRequestResult> {
  try {
    const res = await http.request<ConnectRequestPreview>({
      method: 'GET',
      path: `/v1/oauth/connect/${encodeURIComponent(request_id)}`,
      // The bootstrap 401 for the connect page is handled inline by
      // the component (it saves the return path and renders Login
      // in place), so the http-client's provider-level
      // `on_unauthorized` must not fire here.
      throw_on_unauthorized: true,
    });
    if (
      res === undefined
      || typeof res.request_id !== 'string'
      || typeof res.client_label !== 'string'
      || !Array.isArray(res.offered)
    ) {
      return { kind: 'error', message: 'Empty response body.' };
    }
    return { kind: 'ok', preview: res };
  } catch (err) {
    if (err instanceof ApiError) {
      if (err.kind === 'not_found') {
        // The server distinguishes expired via `reason:'request_expired'`;
        // anything else is a wrong-id 404 the UI shows the same way.
        return { kind: 'expired' };
      }
      if (err.kind === 'forbidden') {
        return { kind: 'forbidden', reason: mapForbiddenReason(err.server_reason) };
      }
      if (err.kind === 'service_unavailable') return { kind: 'disabled' };
      return { kind: 'error', message: explainApiError(err) };
    }
    // `throw_on_unauthorized: true` above skips the provider-level
    // on_unauthorized callback so the consent view can surface the
    // Login screen in place while keeping the return path alive.
    if (err instanceof AuthUnauthorizedError) {
      return { kind: 'unauthorized' };
    }
    if (err instanceof AuthRateLimitError) {
      return { kind: 'error', message: 'Too many requests — wait a moment and retry.' };
    }
    return { kind: 'error', message: err instanceof Error ? err.message : 'Network error.' };
  }
}

/* ---------- decideConnectRequest ---------- */

export type DecideConnectResult =
  | { readonly kind: 'ok'; readonly redirect_url: string }
  | { readonly kind: 'expired' }
  | { readonly kind: 'forbidden'; readonly reason: 'acting_session' | 'support_session' | 'home_session_required' | 'other' }
  | { readonly kind: 'disabled' }
  | { readonly kind: 'invalid_scopes' }
  /**
   * 409 `{error:'conflict', reason:'too_many_connections'}` — this
   * account already has the per-client connection cap live (ADR-0261
   * §8). The dashboard renders the "revoke one in Account settings"
   * copy and does not retry.
   */
  | { readonly kind: 'too_many_connections' }
  | { readonly kind: 'error'; readonly message: string };

export async function decideConnectRequest(
  http: HttpClient,
  request_id: string,
  decision: { readonly decision: 'allow' | 'deny'; readonly scopes?: readonly string[] },
): Promise<DecideConnectResult> {
  const sub = decision.decision === 'allow' ? 'approve' : 'deny';
  try {
    const res = await http.request<ConnectDecisionResponse>({
      method: 'POST',
      path: `/v1/oauth/connect/${encodeURIComponent(request_id)}/${sub}`,
      body: decision.decision === 'allow' ? { scopes: decision.scopes ?? [] } : {},
    });
    if (res === undefined || typeof res.redirect_url !== 'string' || res.redirect_url.length === 0) {
      return { kind: 'error', message: 'Server returned an unexpected decide shape.' };
    }
    return { kind: 'ok', redirect_url: res.redirect_url };
  } catch (err) {
    if (err instanceof ApiError) {
      if (err.kind === 'bad_request') {
        if (err.server_reason === 'invalid_scopes') return { kind: 'invalid_scopes' };
        return { kind: 'error', message: explainApiError(err) };
      }
      if (err.kind === 'not_found') return { kind: 'expired' };
      if (err.kind === 'forbidden') {
        return { kind: 'forbidden', reason: mapForbiddenReason(err.server_reason) };
      }
      if (err.kind === 'conflict' && err.server_reason === 'too_many_connections') {
        return { kind: 'too_many_connections' };
      }
      if (err.kind === 'service_unavailable') return { kind: 'disabled' };
      return { kind: 'error', message: explainApiError(err) };
    }
    if (err instanceof AuthRateLimitError) {
      return { kind: 'error', message: 'Too many requests — wait a moment and retry.' };
    }
    return { kind: 'error', message: err instanceof Error ? err.message : 'Network error.' };
  }
}

/* ---------- helpers ---------- */

/**
 * Map the server's 403 `reason` string to the dashboard's narrower
 * forbidden discriminant. The server emits three variants today
 * (acting / support / home_session_required); anything else is
 * surfaced as `other` so the UI can still render a sensible message.
 */
function mapForbiddenReason(
  reason: string | undefined,
): 'acting_session' | 'support_session' | 'home_session_required' | 'other' {
  if (reason === 'acting_session_cannot_consent') return 'acting_session';
  if (reason === 'support_session_cannot_consent') return 'support_session';
  if (reason === 'consent_requires_home_session') return 'home_session_required';
  return 'other';
}

function mapListError(err: unknown): ListConnectedAppsResult {
  if (err instanceof ApiError) {
    if (err.kind === 'forbidden') return { kind: 'forbidden' };
    if (err.kind === 'service_unavailable') return { kind: 'disabled' };
    return { kind: 'error', message: explainApiError(err) };
  }
  if (err instanceof AuthRateLimitError) {
    return { kind: 'error', message: 'Too many requests — wait a moment and retry.' };
  }
  return { kind: 'error', message: err instanceof Error ? err.message : 'Network error.' };
}

function explainApiError(err: ApiError): string {
  switch (err.kind) {
    case 'service_unavailable':
      return 'Connected apps is temporarily unavailable.';
    case 'network_timeout':
      return 'This may have saved — refresh to check before trying again.';
    case 'network_error':
      return 'Network error — check your connection.';
    case 'bad_response':
      return 'Server returned an unexpected response.';
    default:
      return err.message;
  }
}

/**
 * Chip copy for the Connected apps table (ADR-0261 §5). Shown
 * verbatim per the handoff. The consent page always renders the
 * server's `offered[].label/helper` — this map is for the already-
 * granted scope display where there is no server-provided label.
 */
export const SCOPE_CHIP_LABELS: Readonly<Record<string, string>> = {
  'sites:read': 'See your sites',
  'analytics:read': 'Read agent analytics',
  'security:read': 'Read security activity',
  'security:write': 'Block and unblock senders',
  'orders:read': 'See held orders',
  'readiness:read': 'Read readiness results',
  'readiness:write': 'Run readiness checks',
  'audit:read': 'Search the audit log',
  'accounts:switch': 'Switch between managed accounts',
  offline_access: 'Stay connected',
};
